import { CardContent, LinearProgress, Unstable_Grid2 as Grid, Box } from '@mui/material';
import { useMemo, useCallback } from 'react';

import {
  Card,
  CardHeader,
  CircularProgress,
  Divider,
  Typography,
  DateRangeSelector,
  getDateRanges,
  RangeTypeWithCustom,
} from '@occc/ui-design-system';
import { LocationFragment, Survey_Metrics_Period, SurveyMetricsFragment } from '@occc/graphql';
import { LoadedData } from '@occc-app/routes/Wallet/types';
import { separateEveryThousandByComma } from '@occc-app/lib/numbers';

import { SurveyData, SurveyChart } from './SurveyMetricsChart';
import { MetricInfo } from './MetricInfo';
import { DateRange } from './types';

import { formatDateToMonthDay } from './utils/formatDateToMonthDay';
import { presetToPeriod } from './constants';
import { LocationsFilter } from '../components/LocationsFilter/LocationsFilter';

export type SurveyMetricsSectionProps = {
  surveyMetrics: LoadedData<SurveyMetricsFragment>;
  onPeriodChange: (period: Survey_Metrics_Period) => void;
  customDateRange: DateRange;
  customDateRangeMinSelectableDate?: Date;
  customDateRangeMaxSelectableDate?: Date;
  onCustomDateRangeChange: (value: DateRange) => void;
  locations: LocationFragment[];
  onLocationsChange: (locations: LocationFragment[]) => void;
};

export const SurveyMetricsSection = ({
  surveyMetrics,
  onPeriodChange,
  customDateRange,
  customDateRangeMinSelectableDate,
  customDateRangeMaxSelectableDate,
  onCustomDateRangeChange,
  locations,
  onLocationsChange,
}: SurveyMetricsSectionProps) => {
  const { loading, error, data } = surveyMetrics;
  const isRefetching = loading && data;
  const isInitialLoading = loading && !data;
  const dateRanges = useMemo(() => getDateRanges(), []);

  const chartData: SurveyData[] | null = useMemo(() => {
    if (!data || data.views.counters.length === 0) {
      return null;
    }

    return data.views.counters.map((counter) => {
      return {
        date: formatDateToMonthDay(counter.date),
        surveyViews: counter.count,
      };
    });
  }, [data]);

  const handleDateRangeChange = useCallback(
    ({ startDate, endDate }: DateRange, rangeType: RangeTypeWithCustom) => {
      onCustomDateRangeChange({ startDate, endDate });

      if (presetToPeriod[rangeType]) {
        onPeriodChange(presetToPeriod[rangeType]);
      }
    },
    [onPeriodChange, onCustomDateRangeChange],
  );

  const renderContent = () => {
    if (error) {
      return (
        <Box p={4}>
          <Typography color='error'>Something went wrong. Please try again.</Typography>
        </Box>
      );
    }

    if (isInitialLoading) {
      return (
        <Box display='flex' justifyContent='center' alignItems='center' p={3}>
          <CircularProgress />
        </Box>
      );
    }

    if (!data) {
      return (
        <Box p={4}>
          <Typography color='error'>No data. Something went wrong. Please try again.</Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Grid container spacing={2}>
          <Grid xs={12} sm={3}>
            <MetricInfo
              title='Survey Views'
              value={separateEveryThousandByComma(data.views.totalCount)}
              dailyAverage={data.views.dailyAverageCount}
              tooltipText='The total number of unique members who have viewed the survey.'
            />
          </Grid>

          <Grid xs={12} sm={3}>
            <MetricInfo
              title='Survey Responses'
              value={separateEveryThousandByComma(data.responses.totalCount)}
              dailyAverage={data.responses.dailyAverageCount}
              tooltipText='The total number of unique members who have responded to the survey.'
            />
          </Grid>
        </Grid>

        {chartData && (
          <Box mt={3}>
            <SurveyChart items={chartData} />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Card>
      <CardHeader
        sx={{ py: 2, px: 3, flexWrap: 'wrap', justifyContent: 'flex-end', gap: 2 }}
        title={<Typography variant='subtitle1'>Survey Metrics</Typography>}
        action={
          <Box
            display='flex'
            flexDirection='row'
            gap={2}
            sx={{
              '@media (max-width: 400px)': {
                flexDirection: 'column',
              },
            }}
          >
            <LocationsFilter
              locations={locations}
              onLocationsChange={onLocationsChange}
              disabled={loading}
            />
            <DateRangeSelector
              value={{
                startDate: customDateRange.startDate,
                endDate: customDateRange.endDate,
              }}
              onChange={handleDateRangeChange}
              minSelectableDate={customDateRangeMinSelectableDate}
              maxSelectableDate={customDateRangeMaxSelectableDate}
              dateRangePresets={[
                {
                  ...dateRanges.last_7_days,
                  label: 'Last Week',
                },
                dateRanges.last_30_days,
                dateRanges.last_365_days,
              ]}
              disabled={loading}
            />
          </Box>
        }
      />
      <Divider />

      {isRefetching && <LinearProgress />}

      <CardContent>{renderContent()}</CardContent>
    </Card>
  );
};
